import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  CreditCard,
  CheckCircle2,
  RefreshCw,
  TrendingUp,
  Clock,
  BadgeCheck,
} from "lucide-react";
import { Query } from "appwrite";
import {
  account,
  tablesDB,
  DB_ID,
  USERS_TABLE,
  ORDERS_TABLE,
} from "../../appwrite";
import { useTitle } from "../../hooks/useTitle";
import "./SellerSettings.css";

const fmtNaira = (v) =>
  "₦" +
  parseFloat(v || 0).toLocaleString("en-NG", { minimumFractionDigits: 0 });

const PLATFORM_FEE = 0.1;
const SETTLEMENT_DAYS = 5;

export default function SellerSettings() {
  useTitle("Settings - TrendBay Seller");

  const [userId, setUserId] = useState(null);
  const [fullName, setFullName] = useState("");
  const [existing, setExisting] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    bankCode: "",
    accountNumber: "",
    accountName: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [earnings, setEarnings] = useState({
    settled: 0,
    pending: 0,
    total: 0,
    orderCount: 0,
  });
  const [banks, setBanks] = useState([]);

  useEffect(() => {
    const meta = document.querySelector("meta[name='theme-color']");
    const orig = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "#080F0D");
    return () => {
      if (meta) meta.setAttribute("content", orig);
    };
  }, []);

  useEffect(() => {
    const secretKey = process.env.REACT_APP_PAYSTACK_SECRET_KEY;
    if (secretKey) {
      fetch("https://api.paystack.co/bank?currency=NGN&per_page=100", {
        headers: { Authorization: `Bearer ${secretKey}` },
      })
        .then((r) => r.json())
        .then((d) => {
          if (d.status)
            setBanks(d.data.map((b) => ({ name: b.name, code: b.code })));
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const user = await account.get();
        setUserId(user.$id);

        const [doc, ordersRes] = await Promise.all([
          tablesDB.getRow({
            databaseId: DB_ID,
            tableId: USERS_TABLE,
            rowId: user.$id,
          }),
          tablesDB.listRows({
            databaseId: DB_ID,
            tableId: ORDERS_TABLE,
            queries: [Query.limit(1000)],
          }),
        ]);

        setFullName(doc.fullName || user.name || "Seller");

        if (doc.subaccount_code) {
          setExisting({
            code: doc.subaccount_code,
            bankName: doc.payoutBankName || "",
            accountNumber: doc.payoutAccountNumber || "",
            accountName: doc.payoutAccountName || "",
          });
        } else {
          setEditing(true);
        }

        // Calculate estimated earnings from orders
        const now = new Date();
        const cutoff = new Date(
          now.getTime() - SETTLEMENT_DAYS * 24 * 60 * 60 * 1000,
        );
        let settled = 0,
          pending = 0,
          orderCount = 0;

        (ordersRes.rows || []).forEach((order) => {
          if (order.paymentStatus !== "paid") return;
          const cartItems = (order.cart || []).map((item) => {
            try {
              return JSON.parse(item);
            } catch {
              return item;
            }
          });
          const myItems = cartItems.filter(
            (item) => item.sellerId === user.$id,
          );
          if (myItems.length === 0) return;

          const gross = myItems.reduce(
            (sum, item) =>
              sum + (parseFloat(item.price) || 0) * (item.quantity || 1),
            0,
          );
          const net = gross * (1 - PLATFORM_FEE);
          const orderDate = new Date(order.$createdAt);

          if (orderDate < cutoff) {
            settled += net;
          } else {
            pending += net;
          }
          orderCount++;
        });

        setEarnings({ settled, pending, total: settled + pending, orderCount });
      } catch (e) {
        console.error(e);
        toast.error("Could not load account info");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleField = (e) => {
    const { name, value } = e.target;
    if (name === "accountNumber") {
      if (/^\d{0,10}$/.test(value)) {
        setForm((f) => ({ ...f, accountNumber: value, accountName: "" }));
        setVerified(false);
      }
      return;
    }
    if (name === "bankCode") {
      setForm((f) => ({ ...f, bankCode: value, accountName: "" }));
      setVerified(false);
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Auto-resolve account name when 10-digit number + bank selected
  useEffect(() => {
    const { accountNumber, bankCode } = form;
    if (accountNumber.length !== 10 || !bankCode) return;
    const secretKey = process.env.REACT_APP_PAYSTACK_SECRET_KEY;
    if (!secretKey) return;

    setVerifying(true);
    setVerified(false);
    setForm((f) => ({ ...f, accountName: "" }));

    fetch(`https://api.paystack.co/bank/resolve?account_number=${accountNumber}&bank_code=${bankCode}`, {
      headers: { Authorization: `Bearer ${secretKey}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.status && d.data?.account_name) {
          setForm((f) => ({ ...f, accountName: d.data.account_name }));
          setVerified(true);
        } else {
          toast.error(d.message || "Could not verify account. Check number and bank.");
        }
      })
      .catch(() => toast.error("Verification failed. Check your connection."))
      .finally(() => setVerifying(false));
  }, [form.accountNumber, form.bankCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { bankCode, accountNumber, accountName } = form;
    if (!bankCode || accountNumber.length !== 10 || !accountName.trim()) {
      toast.warn("Fill in all fields. Account number must be 10 digits.");
      return;
    }

    const secretKey = process.env.REACT_APP_PAYSTACK_SECRET_KEY;
    if (!secretKey) {
      toast.error("Paystack key not configured.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch("https://api.paystack.co/subaccount", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${secretKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          business_name: fullName,
          settlement_bank: bankCode,
          account_number: accountNumber,
          percentage_charge: 10,
        }),
      });

      const data = await res.json();
      if (!data.status) {
        toast.error(data.message || "Paystack rejected the bank details.");
        return;
      }

      const subaccount_code = data.data.subaccount_code;
      const bankObj = banks.find((b) => b.code === bankCode);

      await tablesDB.updateRow({
        databaseId: DB_ID,
        tableId: USERS_TABLE,
        rowId: userId,
        data: {
          subaccount_code,
          payoutBankName: bankObj?.name || "",
          payoutAccountNumber: accountNumber,
          payoutAccountName: accountName.trim(),
        },
      });

      setExisting({
        code: subaccount_code,
        bankName: bankObj?.name || "",
        accountNumber,
        accountName: accountName.trim(),
      });
      setEditing(false);
      setForm({ bankCode: "", accountNumber: "", accountName: "" });
      toast.success("Payout account saved!");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ss-page">
        <p className="ss-loading">Loading…</p>
      </div>
    );
  }

  return (
    <div className="ss-page">
      <div className="ss-header">
        <h1 className="ss-title">Settings</h1>
        <p className="ss-subtitle">Manage your seller account</p>
      </div>

      {/* Estimated Earnings */}
      <div className="ss-card ss-earnings-card">
        <div className="ss-card-head">
          <TrendingUp size={18} color="#00A855" />
          <div>
            <h3 className="ss-card-title">Estimated Earnings</h3>
            <p className="ss-card-hint">
              Based on paid orders · 10% platform fee deducted ·{" "}
              {earnings.orderCount} order{earnings.orderCount !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <div className="ss-earnings-total">{fmtNaira(earnings.total)}</div>
        <p className="ss-earn-disclaimer">
          Estimates only. Actual settlement processed by Paystack.
        </p>
      </div>

      {/* Payout Account */}
      <div className="ss-card">
        <div className="ss-card-head">
          <CreditCard size={18} color="#00A855" />
          <div>
            <h3 className="ss-card-title">Payout Account</h3>
            <p className="ss-card-hint">
              Your Nigerian bank account for receiving payments via Paystack
            </p>
          </div>
        </div>

        {/* Saved state */}
        {existing && !editing && (
          <div className="ss-saved">
            <div className="ss-saved-row">
              <CheckCircle2 size={16} color="#00A855" />
              <span className="ss-saved-label">Payout account connected</span>
            </div>
            <div className="ss-saved-detail">
              <span className="ss-detail-bank">{existing.bankName}</span>
              <span className="ss-detail-num">{existing.accountNumber}</span>
              <span className="ss-detail-name">{existing.accountName}</span>
            </div>
            <button className="ss-update-btn" onClick={() => setEditing(true)}>
              <RefreshCw size={13} /> Update account
            </button>
          </div>
        )}

        {/* Form */}
        {editing && (
          <form className="ss-form" onSubmit={handleSubmit}>
            <div className="ss-field">
              <label className="ss-label">
                Bank <span className="ss-req">*</span>
              </label>
              <select
                className="ss-input"
                name="bankCode"
                value={form.bankCode}
                onChange={handleField}
              >
                <option value="">Select your bank</option>
                {banks.map((b) => (
                  <option key={b.code} value={b.code}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="ss-field">
              <label className="ss-label">
                Account Number <span className="ss-req">*</span>
              </label>
              <input
                className="ss-input"
                type="text"
                name="accountNumber"
                value={form.accountNumber}
                onChange={handleField}
                placeholder="10-digit NUBAN"
                inputMode="numeric"
              />
            </div>

            <div className="ss-field">
              <label className="ss-label">
                Account Name <span className="ss-req">*</span>
              </label>
              <div className="ss-input-wrap">
                <input
                  className={`ss-input ${verified ? "ss-input--verified" : ""}`}
                  type="text"
                  name="accountName"
                  value={verifying ? "Verifying…" : form.accountName}
                  readOnly
                  placeholder="Auto-filled after verification"
                />
                {verifying && <span className="ss-spin" />}
                {verified && !verifying && (
                  <BadgeCheck size={16} className="ss-verified-icon" />
                )}
              </div>
            </div>

            <p className="ss-notice">
              TrendBay takes a 10% platform fee. The remainder is settled to
              your account within 5 business days of a completed order.
            </p>

            <div className="ss-form-actions">
              {existing && (
                <button
                  type="button"
                  className="ss-btn-ghost"
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </button>
              )}
              <button type="submit" className="ss-btn-green" disabled={saving}>
                {saving ? "Saving…" : "Save Payout Account"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
